import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useTimelineTasks from 'vl-common/src/components/TimeLine/hooks/useTimelineTasks';
import useTimelineTasksTable from 'vl-common/src/components/TimeLine/hooks/useTimelineTasksTable';
import { TableDataView, HeaderItemType } from 'vl-common/src/components/Table/TableDataView';
import { Row, Col, Container } from 'reactstrap';
import { Avatar, Button, Space } from 'antd';
import useColumnFilters from 'vl-common/src/hooks/useColumnFilters';
import useReactiveColumns from 'vl-common/src/hooks/useReactiveColumns';
import useWidthBreakpoint from 'vl-common/src/hooks/useWidthBreakpoint';
import { RightOutlined, LeftOutlined } from 'antd/icons';
import { SuccessModal } from 'vl-common/src/components/Modals/CustomModals/Success';
import AddTaskModal from 'vl-common/src/components/Tasks/modals/AddTaskModal';
import EditTaskModal from 'vl-common/src/components/Tasks/modals/EditTaskModal';
import CompleteTaskModal from 'vl-common/src/components/Tasks/modals/CompleteTaskModal';
import TaskAuditModal from '../../Tasks/modals/TaskAuditModal';
import ViewTaskModal from 'vl-common/src/components/Tasks/modals/ViewTaskModal';
import CancelTaskModal from 'vl-common/src/components/Tasks/modals/CancelTaskModal';
import { useIsClientAdmin } from 'vl-common/src/hooks/useIsClientAdmin';

interface TaskDisplayProps {
  client_case_id: string;
  case_id: string;
  patient_guid: string | undefined;
}

const TaskDisplay = ({ client_case_id, case_id, patient_guid }: TaskDisplayProps) => {
  const { t } = useTranslation('common');
  const isClientAdmin = useIsClientAdmin();
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showCreateTaskSuccessModal, setShowCreateTaskSuccessModal] = useState(false);
  const [prevCaseId, setPrevCaseId] = useState(() => case_id);
  const {
    taskQueryParams,
    taskHeaders,
    expandedColumns,
    updateTaskQueryParams,
    queryUpdated,
    resetQueryUpdated,
    selectTask,
    useEditModal,
    useViewModal,
    useTaskAuditModal,
    useCompleteTaskModal,
    useCancelTaskModal
  } = useTimelineTasksTable(); // Table related component controls

  const { showEditModal, showEditSuccessModal, onCloseEditModal, onEditSuccess, onCloseEditSuccessModal } =
    useEditModal();
  const { showAuditModal, onCloseAuditModal } = useTaskAuditModal();
  const { showViewTaskModal, onCloseViewTaskModal } = useViewModal();
  const {
    showCompleteTaskModal,
    showCompleteTaskSuccessModal,
    showSaveCompleteTaskSuccessModal,
    onCloseCompleteTaskModal,
    onCompleteTaskSuccess,
    onCloseCompleteTaskSuccessModal,
    onCompleteSaveTaskSuccess
  } = useCompleteTaskModal();

  const {
    cancelTaskModal,
    cancelTaskSuccessModal,
    onCloseCancelTaskModal,
    onCancelTaskCompleted,
    onCloseCancelTaskSuccessModal
  } = useCancelTaskModal();
  const { columns: tableColumns, query, handleTableChange, clearFilters, tableKey } = useColumnFilters(taskHeaders);
  const currentBreakPoint = useWidthBreakpoint();
  const [displayColumnList, hideColumnList] = useReactiveColumns(tableColumns, currentBreakPoint);
  const expandedColumnList = useMemo(() => [...expandedColumns, ...hideColumnList], [expandedColumns, hideColumnList]);

  const {
    taskData,
    refetchTasks,
    currentTaskPage,
    onGetNextPage,
    onGetPreviousPage,
    onGetFirstPage,
    isNextPage,
    isPreviousPage
  } = useTimelineTasks({
    case_id: case_id,
    taskQueryParams: taskQueryParams
  });
  useEffect(() => {
    // Updates the query params when the column query is updated
    updateTaskQueryParams(query);
  }, [query, updateTaskQueryParams]);

  useEffect(() => {
    // Resets to the first page when the case id changes
    if (prevCaseId !== case_id) {
      onGetFirstPage();
      setPrevCaseId(case_id);
    }
    if (queryUpdated) {
      onGetFirstPage();
      resetQueryUpdated();
    }
  }, [onGetFirstPage, queryUpdated, resetQueryUpdated, case_id, prevCaseId]);

  return (
    <>
      <Container
        fluid={true}
        style={{
          padding: 0
        }}
      >
        <Row className="vl-layout-title">
          <Col className="mx-auto p-4 d-flex justify-content-end align-items-baseline" sm="12" md={{ size: 12 }}>
            <Space>
              {isClientAdmin && (
                <Button
                  data-testid="create-task-open"
                  type="primary"
                  onClick={() => {
                    setShowCreateTaskModal(true);
                  }}
                >
                  {t('Create Task')}
                </Button>
              )}
              <Button type="primary" onClick={clearFilters} data-testid="reset-task-filters">
                {t('Clear Filters')}
              </Button>
            </Space>
          </Col>
        </Row>
        <TableDataView
          key={tableKey}
          noDataFoundText="No tasks found"
          headerItems={displayColumnList as HeaderItemType[]}
          hideColumnList={expandedColumnList as []}
          dataSource={taskData?.data?.Tasks || []}
          handleTableChange={handleTableChange}
        />
        <Row className="">
          <Col className="mx-auto p-4 d-flex justify-content-between align-items-baseline" style={{ maxWidth: '85vw' }}>
            <Row className="gap-2" style={{ justifyItems: 'baseline', maxWidth: '150px' }}>
              <Button
                data-testid="previous-task-page"
                type="primary"
                disabled={!isPreviousPage}
                onClick={onGetPreviousPage}
              >
                <LeftOutlined aria-hidden />
                {t('Previous')}
              </Button>
              <Button data-testid="first-task-page" type="primary" disabled={!isPreviousPage} onClick={onGetFirstPage}>
                <LeftOutlined aria-hidden />
                {t('First page')}
              </Button>
            </Row>
            <Avatar shape="square" size={{ xs: 24, sm: 32, md: 40, lg: 40, xl: 40, xxl: 40 }}>
              {currentTaskPage}
            </Avatar>
            <Button data-testid="next-task-page" type="primary" disabled={!isNextPage} onClick={onGetNextPage}>
              {t('Next')}
              <RightOutlined aria-hidden />
            </Button>
          </Col>
        </Row>
      </Container>
      {isClientAdmin && (
        <>
          {/* Creating Tasks - vl-admin only */}
          <AddTaskModal
            key={`create-task-modal-${case_id}`}
            show={showCreateTaskModal}
            onClose={() => setShowCreateTaskModal(false)}
            onCreated={() => {
              setShowCreateTaskModal(false);
              setShowCreateTaskSuccessModal(true);
            }}
            patient_guid={patient_guid}
            case_id={case_id}
            client_case_id={client_case_id}
            data-testid={'create-task-modal'}
          />
          <SuccessModal
            key={`create-task-success-modal-${case_id}`}
            title={'Task Created'}
            onCancel={() => {
              refetchTasks();
              setShowCreateTaskSuccessModal(false);
            }}
            cancelText={'Continue'}
            open={showCreateTaskSuccessModal}
            data-testid={'task-created-success-modal'}
          />
          <EditTaskModal
            key={`edit-task-modal-${selectTask?.task_id}`}
            show={showEditModal}
            selectedTask={selectTask}
            onClose={onCloseEditModal}
            onCompleted={onEditSuccess}
            data-testid={'edit-task-modal'}
          />
          <SuccessModal
            key={`edit-task-success-modal-${selectTask?.task_id}`}
            title={'Task Edited'}
            onCancel={() => {
              refetchTasks();
              onCloseEditSuccessModal();
            }}
            cancelText={'Continue'}
            open={showEditSuccessModal}
            data-testid={'edit-task-success-modal'}
          />

          <CompleteTaskModal
            key={`complete-task-modal-${selectTask?.task_id}`}
            show={showCompleteTaskModal}
            selectedTask={selectTask}
            onClose={onCloseCompleteTaskModal}
            onCompleted={onCompleteTaskSuccess}
            onSaveComplete={onCompleteSaveTaskSuccess}
            data-testid={'complete-task-modal'}
          />
          <SuccessModal
            key={`complete-task-success-modal-${selectTask?.task_id}`}
            title={
              (showCompleteTaskSuccessModal && 'Completed Task Successfully') ||
              (showSaveCompleteTaskSuccessModal && 'Saved Task Successfully')
            }
            onCancel={() => {
              refetchTasks();
              onCloseCompleteTaskSuccessModal();
            }}
            cancelText={'Continue'}
            open={showCompleteTaskSuccessModal || showSaveCompleteTaskSuccessModal}
            data-testid={'complete-task-success-modal'}
          />
          <CancelTaskModal
            key={`cancel-task-modal-${selectTask?.task_id}`}
            data-testid={'cancel-task-modal'}
            show={cancelTaskModal}
            selectedTask={selectTask}
            onClose={onCloseCancelTaskModal}
            onCompleted={onCancelTaskCompleted}
          />
          <SuccessModal
            key={`cancel-task-success-modal-${selectTask?.task_id}`}
            title={'Task Cancelled'}
            onCancel={() => {
              refetchTasks();
              onCloseCancelTaskSuccessModal();
            }}
            cancelText={'Continue'}
            open={cancelTaskSuccessModal}
            data-testid={'cancel-task-success-modal'}
          />
          <TaskAuditModal
            show={showAuditModal}
            selectedTask={selectTask}
            key={selectTask?.task_id}
            onClose={onCloseAuditModal}
            data-testid={'task-audit-modal'}
          />
        </>
      )}
      <ViewTaskModal
        key={`view-task-modal-${selectTask?.task_id}`}
        show={showViewTaskModal}
        selectedTask={selectTask}
        onCompleted={onCloseViewTaskModal}
        onClose={onCloseViewTaskModal}
        data-testid={'view-task-modal'}
      />
    </>
  );
};

export default TaskDisplay;
